<template>
  <div class="detail-page">
    <div class="detail-inner">
      <!-- 顶部品牌：与首页/下载页同口径，LOGO 位用图标库字形，不依赖位图资源 -->
      <div class="detail-head">
        <i class="ca ca-logo head-logo"></i>
        <span class="head-name">{{ cfg.appName }}</span>
      </div>

      <div class="detail-card">
        <!-- 加载中 -->
        <div v-if="loading" class="detail-tip">加载中...</div>

        <!-- 加载失败 / 无内容 -->
        <div v-else-if="!title && !content" class="detail-tip">{{ errMsg || '内容不存在' }}</div>

        <!-- 协议正文 -->
        <template v-else>
          <h1 class="detail-title">{{ title }}</h1>
          <div class="detail-content" v-html="content"></div>
        </template>
      </div>

      <div class="detail-footer">
        <div>© {{ year }} {{ cfg.companyShort }} 版权所有</div>
        <div>
          <a class="beian" :href="cfg.beianUrl" target="_blank" rel="noopener noreferrer">{{ cfg.icpNo }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    httpRequest
  } from '@/utils/app'
  import config from '@/utils/config'

  export default {
    name: 'PagesDetail',
    data() {
      return {
        cfg: config.appDownload,
        loading: true,
        title: '',
        content: '',
        errMsg: ''
      }
    },
    computed: {
      year() {
        return new Date().getFullYear()
      }
    },
    created() {
      // 路由 /pages/detail/:id.html 中的 id 即 ca_pageset 记录主键（协议文档）
      const id = this.$route.params.id
      this.loadDetail(id)
    },
    methods: {
      loadDetail(id) {
        if (!id) {
          this.loading = false
          this.errMsg = '缺少文档编号'
          return
        }
        this.loading = true
        httpRequest({
          action: 'pageset_detail',
          id: id
        }, 'GET').then(response => {
          this.loading = false
          if (response && response.error === false && response.data) {
            this.title = response.data.title || ''
            this.content = response.data.content || ''
            // 同步浏览器标题，便于分享/收藏时识别
            if (this.title) {
              document.title = this.title
            }
          } else {
            this.errMsg = (response && response.msg) || '内容不存在'
          }
        })
      }
    }
  }
</script>

<style scoped>
  .detail-page {
    min-height: 100vh;
    background: #f6f6f8;
    padding: 20px 14px 40px;
    box-sizing: border-box;
  }

  .detail-inner {
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
  }

  .detail-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
  }

  .head-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(219, 52, 110, 0.2);
    background: #fff;
    color: #dc2380;
    font-size: 24px;
    line-height: 1;
  }

  .head-name {
    font-size: 17px;
    font-weight: 600;
    color: #db346e;
    letter-spacing: 1px;
  }

  .detail-card {
    background: #fff;
    border-radius: 14px;
    padding: 24px 20px 30px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    min-height: 200px;
  }

  .detail-tip {
    text-align: center;
    color: #999;
    font-size: 14px;
    padding: 60px 0;
  }

  .detail-title {
    font-size: 22px;
    font-weight: 700;
    color: #222;
    text-align: center;
    margin: 0 0 22px;
    line-height: 1.4;
  }

  /* 富文本正文：接口返回的是已解码 HTML，需覆盖其内联/默认排版以保证可读性 */
  .detail-content {
    font-size: 15px;
    line-height: 1.85;
    color: #444;
    word-break: break-word;
  }

  .detail-content >>> img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 12px auto;
  }

  .detail-content >>> h1,
  .detail-content >>> h2,
  .detail-content >>> h3 {
    color: #222;
    font-weight: 700;
    margin: 20px 0 10px;
    line-height: 1.5;
  }

  .detail-content >>> h1 {
    font-size: 19px;
  }

  .detail-content >>> h2 {
    font-size: 17px;
  }

  .detail-content >>> h3 {
    font-size: 16px;
  }

  .detail-content >>> p {
    margin: 0 0 12px;
    text-indent: 2em;
  }

  .detail-content >>> ul,
  .detail-content >>> ol {
    padding-left: 24px;
    margin: 0 0 12px;
  }

  .detail-content >>> a {
    color: #db346e;
    text-decoration: none;
  }

  /* 协议正文常带表格 / 代码块 / 引用，窄屏下必须可横滚，不能顶宽溢出 */
  .detail-content >>> table {
    display: block;
    width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 0 0 12px;
    font-size: 14px;
  }

  .detail-content >>> th,
  .detail-content >>> td {
    border: 1px solid #ebeef5;
    padding: 6px 10px;
    text-align: left;
    word-break: break-word;
  }

  .detail-content >>> pre {
    margin: 0 0 12px;
    padding: 12px;
    background: #f7f7f9;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.7;
  }

  .detail-content >>> blockquote {
    margin: 0 0 12px;
    padding: 8px 12px;
    border-left: 3px solid #f4a9c4;
    background: #fff8fa;
    color: #666;
  }

  .detail-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0 18px;
    text-align: center;
    color: #aaa;
    font-size: 12px;
    margin-top: 24px;
    line-height: 1.9;
  }

  .detail-footer .beian,
  .detail-footer .beian:visited {
    color: #aaa;
    text-decoration: none;
  }

  .detail-footer .beian:hover {
    color: #db346e;
  }

  /* PC 端：阅读栏加宽、内边距与字号上调，长时间看协议不至于拥挤 */
  @media only screen and (min-width: 768px) {
    .detail-page {
      padding: 36px 24px 48px;
    }

    .detail-inner {
      max-width: 860px;
    }

    .detail-card {
      padding: 34px 40px 40px;
      border-radius: 16px;
    }

    .detail-title {
      font-size: 26px;
      margin-bottom: 26px;
    }

    .detail-content {
      font-size: 16px;
    }
  }

  @media (max-width: 480px) {
    .detail-page {
      padding: 14px 10px 28px;
      padding-bottom: calc(28px + env(safe-area-inset-bottom));
    }

    .detail-head {
      margin-bottom: 12px;
    }

    .head-logo {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      font-size: 20px;
    }

    .head-name {
      font-size: 15px;
    }

    .detail-card {
      padding: 20px 16px 26px;
    }

    .detail-title {
      font-size: 19px;
    }

    .detail-content {
      font-size: 14px;
      line-height: 1.8;
    }

    .detail-content >>> ul,
    .detail-content >>> ol {
      padding-left: 18px;
    }
  }
</style>
